import {
  ActionsDropdownMenu,
  ActionsDropdownMenuItemProps,
  BrandSymbol,
  Card,
  Group,
  GroupProps,
  NakedButton,
  Text,
  Typography,
  useBreakpointValue,
} from '@melio/penny';
import { useMelioIntl } from '@melio/platform-i18n';
import { SubscriptionBillingCycleEnum } from '@melio/subscriptions-api';
import { useSubscription } from '@melio/subscriptions-sdk';
import { usePlanInfo, useSubscriptionRouter } from '@melio/subscriptions-ui';
import React, { useState } from 'react';

type EditablePlanCardProps = {
  onOpenCancelModal: () => void;
  onOpenSwitchBillingCycleModal: () => void;
};

export const EditablePlanCard = ({ onOpenCancelModal, onOpenSwitchBillingCycleModal }: EditablePlanCardProps) => {
  const { goToSubscriptionPlans } = useSubscriptionRouter();
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const { subscription } = useSubscription();
  const { formatMessage, formatDate, formatCurrency } = useMelioIntl();
  const groupVariant: GroupProps['variant'] = useBreakpointValue({ xs: 'vertical', s: 'horizontal' });

  const { planName } = usePlanInfo({ planId: subscription.planId });

  const onManagePlan = () => {
    goToSubscriptionPlans({ returnUrl: '/settings/subscription-plans' });
  };

  const actions: ActionsDropdownMenuItemProps[] = [
    {
      label: formatMessage('widgets.editablePlanCard.action.managePlan'),
      dataTestId: 'subscription-plans-manage',
      onClick: onManagePlan,
    },
    {
      label:
        subscription.billingCycle === SubscriptionBillingCycleEnum.Monthly
          ? formatMessage('widgets.editablePlanCard.action.changeBillingCycle.switchToMonthly')
          : formatMessage('widgets.editablePlanCard.action.changeBillingCycle.switchToYearly'),
      dataTestId: 'subscription-plans-switch-billing-cycle',
      onClick: onOpenSwitchBillingCycleModal,
    },
    {
      label: formatMessage('widgets.editablePlanCard.action.removePlan'),
      dataTestId: 'subscription-plans-remove',
      variant: 'critical',
      onClick: onOpenCancelModal,
    },
  ];

  return (
    <Card variant="flat" paddingX="m" paddingY="m" data-testid="subscription-current-plan">
      <Group width="full" variant="vertical" spacing="m" alignItems="flex-start">
        <Group width="full" alignItems="center" justifyContent="space-between">
          <Group spacing="s" alignItems="center">
            <BrandSymbol type="melio" size="extra-large" />
            <Text textStyle="body2Semi">{planName}</Text>
          </Group>

          <ActionsDropdownMenu
            isOpen={isMenuOpen}
            onOpenChange={setIsMenuOpen}
            size="small"
            label="menu action"
            data-testid="subscription-current-plan-actions-menu"
            items={actions}
          />
        </Group>

        <Group hasDivider spacing="m" width="full" variant={groupVariant}>
          <CurrentPlanCell label={formatMessage('widgets.editablePlanCard.title.paymentFrequency')}>
            <Text textStyle="body2">
              {formatMessage(`widgets.editablePlanCard.paymentFrequency.${subscription.billingCycle}`)}
            </Text>
          </CurrentPlanCell>

          <CurrentPlanCell label={formatMessage('widgets.editablePlanCard.title.seats')}>
            <Text textStyle="body2">{subscription.numberOfSeats}</Text>
            <NakedButton variant="secondary" label={formatMessage('widgets.editablePlanCard.seats.manage')} />
          </CurrentPlanCell>

          <CurrentPlanCell label={formatMessage('widgets.editablePlanCard.title.nextPayment')}>
            <Text textStyle="body2">{formatCurrency(subscription.nextBillingAmount)}</Text>
            <Text textStyle="body4" color="neutral.darker">
              {formatMessage('widgets.editablePlanCard.action.plan.nextBillingPrefix', {
                nextBillingDate: formatDate(subscription.nextBillingDate, {
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                }),
              })}
            </Text>
          </CurrentPlanCell>
        </Group>
      </Group>
    </Card>
  );
};

const CurrentPlanCell = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <Group width="full" variant="vertical" alignItems="flex-start" spacing="xxs">
    <Typography.Label label={label} />
    {children}
  </Group>
);
